import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { CardScore } from '@sorare/core/src/components/scoring/CardScore';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { isType } from '@sorare/core/src/lib/gql';

import { InjuryTag } from 'components/player/InjuryTag';
import { TokenStatsIcon } from 'components/token/TokenStatsIcon';

import { CardBonus } from '../CardBonus';
import { ItemEligibility } from '../ItemEligibility';
import { U23Eligible } from '../U23Eligible';
import type {
  CardPropertiesWithStats_anyCard,
  CardProperties_anyCard,
} from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ gap: 0 })`
  color: var(--c-white);
  flex-wrap: wrap;
  gap: var(--half-unit);
`;

type Props = {
  card: CardProperties_anyCard;
  withTransferMalus: boolean;
  onInfoClick?: () => void;
  cardWithStats?: CardPropertiesWithStats_anyCard;
  playerScoreSmall?: boolean;
};

export const CardProperties = ({
  card,
  withTransferMalus,
  onInfoClick,
  cardWithStats,
  playerScoreSmall,
}: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();

  const modePerSport: Record<Sport, TotalAveragePlayerScore> = {
    [Sport.FOOTBALL]: AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
    [Sport.NBA]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.BASEBALL]: useMlbSeasonAverageScore
      ? AveragePlayerScore.SEASON_AVERAGE_SCORE
      : AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
  };
  const mode = modePerSport[sport];

  return (
    <Root>
      {!!cardWithStats && (
        <TokenStatsIcon card={cardWithStats} onInfoClick={onInfoClick} />
      )}
      <CardScore small={playerScoreSmall} card={card} mode={mode} />
      <CardBonus
        card={card}
        withTransferMalus={withTransferMalus}
        size="large"
      />
      {isType(card, 'Card') && (
        <>
          {!!card.u23Eligible && <U23Eligible />}
          <ItemEligibility cards={[card]} />
        </>
      )}
      <InjuryTag player={card.anyPlayer} size="small" />
    </Root>
  );
};

CardProperties.fragments = {
  anyCard: gql`
    fragment CardProperties_anyCard on AnyCardInterface {
      slug
      ... on Card {
        slug
        u23Eligible
        ...ItemEligibility_card
      }
      anyPlayer {
        slug
        ...InjuryTag_anyPlayer
      }
      ...CardBonus_anyCard
      ...CardScore_anyCard
    }
    ${ItemEligibility.fragments.card}
    ${CardScore.fragments.anyCard}
    ${CardBonus.fragments.anyCard}
    ${InjuryTag.fragments.anyPlayer}
  ` as TypedDocumentNode<CardProperties_anyCard>,
  anyCardWithStats: gql`
    fragment CardPropertiesWithStats_anyCard on AnyCardInterface {
      slug
      ...TokenStatsIcon_anyCard
    }
    ${TokenStatsIcon.fragments.anyCard}
  ` as TypedDocumentNode<CardPropertiesWithStats_anyCard>,
};
